/**
 * THE ONE PLACE A DIRECTOR'S PREVIEW REFUSES TO ACT.
 *
 * Packet J13's first pass wrapped the whole rendered dancer home in `inert`,
 * which did stop every write and also stopped every EXPANDER, TAB and JUMP
 * LINK, so a Director could not read past the first screen (Codex, PR #1090,
 * P1). The rule that replaced it is narrower and says what it means:
 *
 *   IN-PAGE MOVEMENT IS ALLOWED. Opening a section, switching a class tab,
 *   following a jump link inside the preview, scrolling: none of that leaves
 *   the route and none of it writes, and all of it is exactly what "see what
 *   she sees" means.
 *
 *   WRITES AND ROUTE CHANGES ARE INTERCEPTED. Every dancer write in this app
 *   leaves through src/lib/dancerSession.ts's POST helpers or through the
 *   attachment reservation gateway, so both check this flag before they open
 *   a connection. Links that leave /preview/dancer/:studentId are stopped by
 *   the shell's own capture handler.
 *
 * WHY A MODULE FLAG AND NOT A CONTEXT. The write helpers are plain async
 * functions called from fifteen sections and their composers, none of which
 * this packet owns. A React context would have to be threaded through every
 * one of them, and the section that forgot would be the one that wrote. A
 * flag at the transport is unmissable: there is no way to reach the network
 * from a dancer surface without passing it.
 *
 * NOTHING THROWS. A refusal announces itself once, on the window, and the
 * preview shell turns that into a small in-flow note. A thrown error would
 * be rendered by whichever section made the call as its own failure message,
 * which reads as "the studio is broken" rather than "not part of the
 * preview".
 */

export const DANCER_PREVIEW_BLOCKED_EVENT = 'ldg:dancer-preview-blocked';

/** The note the shell shows, and the words a refused call is described by. */
export const DANCER_PREVIEW_BLOCKED_NOTE = 'Not part of the preview';

/**
 * A counter, not a boolean: React can mount the next preview before the last
 * one's cleanup runs during a route change, and a boolean would be switched
 * off by the departing page while the arriving one still needs it on.
 */
let openPreviews = 0;

export function beginDancerPreview(): void {
  openPreviews += 1;
}

export function endDancerPreview(): void {
  openPreviews = Math.max(0, openPreviews - 1);
}

export function dancerPreviewActive(): boolean {
  return openPreviews > 0;
}

/** Test-only reset, so one test's leaked preview cannot silence the next. */
export function resetDancerPreviewForTests(): void {
  openPreviews = 0;
}

/**
 * True when the caller must NOT proceed, and it has already said so.
 *
 * Every mutating helper starts with this. It is deliberately a question with
 * a side effect rather than two calls, so there is no shape in which a caller
 * checks the flag and forgets to tell anyone.
 */
export function blockedByDancerPreview(): boolean {
  if (openPreviews === 0) return false;
  announceDancerPreviewBlocked();
  return true;
}

/** Say a preview refused something, without deciding anything. */
export function announceDancerPreviewBlocked(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(DANCER_PREVIEW_BLOCKED_EVENT));
  } catch {
    // A window without CustomEvent is not a reason to let a write through:
    // the caller has already been told to stop by the return value above.
  }
}

/**
 * Does this href stay inside the preview the Director is looking at?
 *
 * PURE, and the reason the shell's capture handler can be tested. An in-page
 * anchor ('#dancer-classes') stays; the same path with a different hash
 * stays; anything that changes the path, or leaves for another origin,
 * leaves. A relative href is resolved against the current path first, so
 * '../account' is judged by where it actually lands rather than by how it is
 * spelled.
 */
export function hrefStaysInPreview(href: string, previewPath: string, origin: string): boolean {
  const raw = (href ?? '').trim();
  if (!raw) return false;
  // A bare fragment never leaves the page it is on.
  if (raw.startsWith('#')) return true;
  let url: URL;
  try {
    url = new URL(raw, `${origin}${previewPath}`);
  } catch {
    return false;
  }
  if (url.origin !== origin) return false;
  return url.pathname === previewPath;
}
